import { useCallback, useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ClientDialog from "@/components/ClientDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import ResponsiveDataView from "@/components/responsive/ResponsiveDataView";
import ResponsivePageHeader from "@/components/responsive/ResponsivePageHeader";
import {
  formatDate,
  formatDateTime,
  useLanguage,
  useTranslation,
} from "@/i18n/LanguageContext";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import usePaginatedResource from "@/hooks/usePaginatedResource";

function formatGender(gender, t) {
  return gender ? t(`common.states.${gender}`) : "-";
}

export default function Clients() {
  const t = useTranslation();
  const { language } = useLanguage();
  const { page, loading, reload: reloadClients, changePage } = usePaginatedResource(
    async (params, { signal }) => (await api.get("/clients", { params, signal })).data,
    { onError: (err) => toast.error(extractApiError(err, t("clients.toast.failedLoad"))) },
  );
  const [publicLink, setPublicLink] = useState("");
  const [linkLoading, setLinkLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadPublicLink = useCallback(async () => {
    setLinkLoading(true);
    try {
      const { data } = await api.get("/clients/public-link");
      setPublicLink(data.url);
    } catch (err) {
      toast.error(extractApiError(err, t("clients.toast.couldNotLoadLink")));
    } finally {
      setLinkLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPublicLink();
  }, [loadPublicLink]);

  const openDetail = async (client) => {
    try {
      const { data } = await api.get(`/clients/${client.id}`);
      setEditing(data);
    } catch (err) {
      toast.error(extractApiError(err, t("clients.toast.couldNotLoadDetail")));
    }
  };

  const save = async (values) => {
    try {
      if (editing?.id) {
        await api.patch(`/clients/${editing.id}`, values);
      } else {
        await api.post("/clients", values);
      }
      setEditing(null);
      reloadClients();
      toast.success(
        editing?.id ? t("clients.toast.updated") : t("clients.toast.created"),
      );
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  const remove = async (client) => {
    try {
      await api.delete(`/clients/${client.id}`);
      setConfirmDelete(null);
      reloadClients();
      toast.success(t("clients.toast.deleted"));
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  const copyPublicLink = async () => {
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      toast.success(t("common.toasts.copiedPublicLink"));
    } catch {
      toast.error(t("common.toasts.couldNotCopyPublicLink"));
    }
  };

  const openPublicLink = () => {
    if (!publicLink) return;
    window.open(publicLink, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="clients-page" className="space-y-6">
      <ResponsivePageHeader
        title={t("clients.title")}
        description={t("clients.description")}
        action={
          <Button
            onClick={() => setEditing({})}
            className="w-full rounded-lg sm:w-auto"
            data-testid="clients-create-button"
          >
            <Plus className="mr-1 h-4 w-4" /> {t("clients.newClient")}
          </Button>
        }
      />

      <section
        className="space-y-3 rounded-xl border border-border bg-card p-4"
        data-testid="clients-public-link-card"
      >
        <div>
          <div className="mono-eyebrow mb-1">
            {t("clients.publicLink.eyebrow")}
          </div>
          <h2 className="font-display text-lg tracking-tight">
            {t("clients.publicLink.title")}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("clients.publicLink.description")}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            value={linkLoading ? t("clients.publicLink.loading") : publicLink}
            readOnly
            className="font-mono text-[11px]"
            data-testid="clients-public-link-input"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              onClick={copyPublicLink}
              disabled={linkLoading || !publicLink}
              data-testid="clients-public-link-copy"
            >
              {t("clients.publicLink.copy")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="rounded-lg"
              onClick={openPublicLink}
              disabled={linkLoading || !publicLink}
              data-testid="clients-public-link-open"
            >
              {t("clients.publicLink.open")}
            </Button>
          </div>
        </div>
      </section>

      <ResponsiveDataView
        items={page.items}
        loading={loading}
        emptyState={t("clients.empty")}
        getRowKey={(client) => client.id}
        getRowTestId={(client) => `client-row-${client.id}`}
        tableTestId="clients-table"
        loadingTestId="clients-loading"
        emptyTestId="clients-empty"
        columns={[
          {
            key: "name",
            header: t("clients.columns.name"),
            cell: (client) => client.name,
          },
          {
            key: "email",
            header: t("clients.columns.email"),
            cell: (client) => client.email,
          },
          {
            key: "gender",
            header: t("common.fields.gender"),
            cell: (client) => formatGender(client.gender, t),
          },
          {
            key: "device",
            header: t("clients.columns.device"),
            cell: (client) => client.device || "-",
          },
          {
            key: "birthDate",
            header: t("clients.columns.birthDate"),
            cell: (client) => (
              <span className="font-mono text-xs">
                {formatDate(client.birthDate, language)}
              </span>
            ),
          },
          {
            key: "created",
            header: t("clients.columns.created"),
            cell: (client) => (
              <span className="font-mono text-[11px] text-muted-foreground">
                {formatDateTime(client.createdAt, language)}
              </span>
            ),
          },
        ]}
        mobileTitle={(client) => client.name}
        mobileSubtitle={(client) => client.email}
        mobileFields={[
          { label: t("common.fields.email"), value: (client) => client.email },
          {
            label: t("common.fields.device"),
            value: (client) => client.device || "-",
          },
          {
            label: t("common.fields.gender"),
            value: (client) => formatGender(client.gender, t),
          },
          {
            label: t("common.fields.birthDate"),
            value: (client) => formatDate(client.birthDate, language),
          },
          {
            label: t("common.fields.created"),
            value: (client) => formatDateTime(client.createdAt, language),
            priority: "secondary",
          },
        ]}
        rowActions={[
          {
            key: "open",
            label: t("common.actions.open"),
            icon: Pencil,
            onClick: openDetail,
            testId: (client) => `open-client-${client.id}`,
          },
          {
            key: "delete",
            label: t("common.actions.delete"),
            icon: Trash2,
            destructive: true,
            onClick: setConfirmDelete,
            testId: (client) => `delete-client-${client.id}`,
          },
        ]}
        pagination={{
          total: page.total,
          limit: page.limit,
          offset: page.offset,
          onChange: changePage,
          testId: "clients-pager",
        }}
      />

      <ClientDialog
        client={editing}
        onClose={() => setEditing(null)}
        onSave={save}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t("clients.delete.title")}
        description={t("clients.delete.description", {
          name: confirmDelete?.name,
        })}
        confirmLabel={t("common.actions.delete")}
        destructive
        onConfirm={() => remove(confirmDelete)}
        testId="client-delete-dialog"
      />
    </div>
  );
}
